import { Box, FormGroup, Typography } from '@mui/material';
import React, { useState, useEffect } from 'react';
import CustomAutocomplete from 'components/shared/CustomAutocomplete/CustomAutocomplete';
import CustomButton from 'components/shared/CustomButton/CustomButton';
import CustomDateRangePicker from 'components/shared/CustomDateRangePicker/CustomDateRangePicker';
import CustomPopOver from 'components/shared/CustomPopOver/CustomPopOver';
import { FilterPopoverProps, FilterOption, FilterState } from '../DealsFilters.types';
import zeplinColor from 'theme/Color';
import { hasActiveFilters, createInitialFilterState } from '../DealsFilters.utils';

const DEAL_HEALTH_OPTIONS: FilterOption[] = [
  { Name: 'On track', Id: 'onTrack' },
  { Name: 'Warning', Id: 'warning' },
  { Name: 'Overdue', Id: 'overdue' },
];

export const FilterPopover = ({
  anchorEl,
  onClose,
  filterState,
  onFilterChange,
  onClearAll,
  ownerOptions,
  clientOptions,
  ownerLoading,
  clientLoading,
  ownerTotalLength,
  clientTotalLength,
  onOwnerSearch,
  onClientSearch,
}: FilterPopoverProps) => {
  // Temporary filter state that only applies when "Apply" is clicked
  const [tempFilterState, setTempFilterState] = useState<FilterState>(filterState);

  // Reset temporary state when popover opens or filterState changes
  useEffect(() => {
    if (anchorEl) {
      setTempFilterState(filterState);
    }
  }, [anchorEl, filterState]);

  const activeFilters = hasActiveFilters(tempFilterState);

  const updateTempFilter = (updates: Partial<FilterState>) => {
    setTempFilterState((prev) => ({ ...prev, ...updates }));
  };

  const handleDealHealthSelection = (item: any) => {
    updateTempFilter({
      dealHealth: tempFilterState.dealHealth?.Id === item.Id ? null : item,
    });
  };

  const handleDealHealthDelete = () => {
    updateTempFilter({ dealHealth: null });
  };

  const handleOwnerSelection = (item: any) => {
    updateTempFilter({
      owner: tempFilterState.owner?.Id === item.Id ? null : item,
    });
  };

  const handleOwnerDelete = () => {
    updateTempFilter({ owner: null });
  };

  const handleClientSelection = (item: any) => {
    updateTempFilter({
      client: tempFilterState.client?.Id === item.Id ? null : item,
    });
  };

  const handleClientDelete = () => {
    updateTempFilter({ client: null });
  };

  const handleClearAll = () => {
    const clearedState = createInitialFilterState();
    setTempFilterState(clearedState);
    onFilterChange(clearedState);
    onClose();
  };

  const handleApply = () => {
    // Apply all temporary filter changes by passing the complete state
    // Create a new object to ensure React detects the change
    const newFilterState: Partial<FilterState> = {
      dealHealth: tempFilterState.dealHealth ?? null,
      owner: tempFilterState.owner ?? null,
      client: tempFilterState.client ?? null,
      startDate: tempFilterState.startDate ?? null,
      closeDate: tempFilterState.closeDate ?? null,
    };
    onFilterChange(newFilterState);
    onClose();
  };

  const handleSetAnchorEl: React.Dispatch<React.SetStateAction<HTMLButtonElement | null>> = (
    el: React.SetStateAction<HTMLButtonElement | null>,
  ) => {
    const newValue = typeof el === 'function' ? el(anchorEl) : el;
    if (!newValue) {
      onClose();
    }
  };

  return (
    <CustomPopOver
      setAnchorEl={handleSetAnchorEl}
      anchorEl={anchorEl}
      id='simple-popover'
      sx={{
        borderRadius: '14px',
        boxShadow: 'rgba(0, 0, 0, 0.15)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            padding: '16px',
            width: { xs: '250px', xl: '283px' },
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            height: '100%',
            overflow: 'auto',
            '& .MuiAutocomplete-root': {
              minHeight: '48px',
            },
            '& .MuiInputBase-root': {
              minHeight: '48px',
              overflow: 'visible',
              alignItems: 'flex-start',
            },
            '& .MuiAutocomplete-inputRoot': {
              flexWrap: 'nowrap',
              minHeight: '48px',
              paddingTop: '6px',
              paddingBottom: '6px',
              alignItems: 'center',
              gap: '6px',
              overflowX: 'auto',
              overflowY: 'hidden',
            },
            '& .MuiFormControl-root': {
              marginBottom: '0px',
            },
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Typography variant='responsiveHeading3' color={zeplinColor.BasicBlack}>
              Filter
            </Typography>
            <Typography
              variant='responsiveButtonSmall'
              color={activeFilters ? zeplinColor.Brand1 : zeplinColor.Neutral8}
              onClick={activeFilters ? handleClearAll : undefined}
              sx={{
                cursor: activeFilters ? 'pointer' : 'default',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Clear All
            </Typography>
          </Box>

          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
              width: '100%',
            }}
          >
            {/* Deal Health */}
            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <CustomAutocomplete
                options={DEAL_HEALTH_OPTIONS}
                label='Deal Health'
                getOptionLabel={(option: any) => option?.Name || ''}
                handleDeleteItems={handleDealHealthDelete}
                selectedItems={tempFilterState}
                handleSelection={handleDealHealthSelection}
                value={tempFilterState.dealHealth}
                multiple={false}
                handleFilter={() => {}}
                placeholder='Deal Health'
                currentLength={DEAL_HEALTH_OPTIONS.length}
                totalLength={DEAL_HEALTH_OPTIONS.length}
                next={() => {}}
                loading={false}
                popupIcon='/assets/down-arrow.svg'
                inputBorder={14}
              />
            </Box>

            {/* Owner */}
            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <CustomAutocomplete
                options={ownerOptions}
                label='Owner'
                getOptionLabel={(option: any) => option?.Name || ''}
                handleDeleteItems={handleOwnerDelete}
                selectedItems={tempFilterState}
                handleSelection={handleOwnerSelection}
                value={tempFilterState.owner}
                multiple={false}
                handleFilter={(_, value: string) => onOwnerSearch(value)}
                placeholder='Owner'
                currentLength={ownerOptions.length}
                totalLength={ownerTotalLength}
                next={() => {}}
                loading={ownerLoading}
                popupIcon='/assets/down-arrow.svg'
                inputBorder={14}
              />
            </Box>

            {/* Client */}
            <Box sx={{ width: '100%', minHeight: '48px' }}>
              <CustomAutocomplete
                options={clientOptions}
                label='Client'
                getOptionLabel={(option: any) => option?.Name || ''}
                handleDeleteItems={handleClientDelete}
                selectedItems={tempFilterState}
                handleSelection={handleClientSelection}
                value={tempFilterState.client}
                multiple={false}
                handleFilter={(_, value: string) => onClientSearch(value)}
                placeholder='Client'
                currentLength={clientOptions.length}
                totalLength={clientTotalLength}
                next={() => {}}
                loading={clientLoading}
                popupIcon='/assets/down-arrow.svg'
                inputBorder={14}
              />
            </Box>

            {/* Date Range */}
            <CustomDateRangePicker
              startDate={tempFilterState.startDate}
              closeDate={tempFilterState.closeDate}
              onStartDateChange={(date) => {
                updateTempFilter({ startDate: date });
              }}
              onCloseDateChange={(date) => {
                updateTempFilter({ closeDate: date });
              }}
              label='Close date'
              placeholder='Close date'
              size='medium'
            />
          </FormGroup>

          {/* Apply Button */}
          <Box sx={{ padding: '16px', paddingTop: 0 }}>
            <CustomButton
              disabled={false}
              variant='contained'
              text='Apply'
              onClick={handleApply}
              style={{
                borderRadius: '14px',
                height: '48px',
                width: '100%',
              }}
              buttonVariant='buttonMedium'
            />
          </Box>
        </Box>
      </Box>
    </CustomPopOver>
  );
};
